import { LifeBuoy } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import { esDeAgencia, marca } from '../marca/marca'

/**
 * A dónde se nos escribe (§28: un mensaje dice qué hacer, y «escríbenos» sin decir dónde
 * no lo dice). Es el formulario de contacto de la web comercial, con el motivo «consulta»
 * ya elegido y la pantalla de la que viene en la dirección (`utm_content`, que el
 * formulario guarda con el envío), y un correo si la construcción del panel lo trae:
 *
 *   VITE_AYUDA_URL=https://wixy.app/es/contacto   (por omisión)
 *   VITE_CORREO_DE_AYUDA=hola@wixy.app            (opcional)
 *
 * No importa nada de `ui.tsx`: el `Aviso` de allí usa `conAyuda` de aquí.
 */
const DIRECCION = ((import.meta.env.VITE_AYUDA_URL as string | undefined) || 'https://wixy.app/es/contacto').trim()
export const CORREO_DE_AYUDA = ((import.meta.env.VITE_CORREO_DE_AYUDA as string | undefined) || '').trim()

/**
 * En el panel de una agencia revendedora (§36) se le escribe **a ella**: su página de ayuda, su WhatsApp o
 * su correo, lo que tenga. Sin ninguno, cadena vacía: «escríbenos» queda como texto, nunca hacia Wixy.
 */
const ayudaDeLaAgencia = (): null | string => {
  const m = marca()
  if (!m) return null
  if (m.soporte.url) return m.soporte.url
  const digitos = (m.soporte.whatsapp ?? '').replace(/\D/g, '')
  if (digitos) return `https://wa.me/${digitos}`
  return m.soporte.correo ? `mailto:${m.soporte.correo}` : ''
}

/** El correo de ayuda que se enseña: el de la agencia en su panel, el nuestro en el de Wixy. */
export const correoDeAyuda = (): string => (esDeAgencia() ? (marca()?.soporte.correo ?? '') : CORREO_DE_AYUDA)

const ENLACE = 'font-medium text-violeta underline underline-offset-2 hover:text-violeta-2'

export const enlaceDeAyuda = (contexto?: string): string => {
  const deLaAgencia = ayudaDeLaAgencia()
  if (deLaAgencia !== null) return deLaAgencia
  try {
    const url = new URL(DIRECCION)
    url.searchParams.set('motivo', 'consulta')
    url.searchParams.set('utm_source', 'panel')
    if (contexto) url.searchParams.set('utm_content', contexto.slice(0, 60))
    return url.toString()
  } catch {
    return DIRECCION
  }
}

/** La pantalla en la que está el usuario, sin ids: «diseno», «s-dominio», «creditos»… */
export const pantallaDe = (ruta: string): string =>
  ruta
    .split('/')
    .filter((t) => t && !/^\d+$/.test(t) && t !== 'e' && t !== 's')
    .slice(-2)
    .join('-') || 'inicio'

/**
 * «escríbenos», con enlace. Va dentro de una frase —también dentro de un `Aviso`, que
 * pinta un `<p>`—, así que es solo texto en línea.
 */
export const Escribenos = ({ contexto, children = 'escríbenos' }: { children?: ReactNode; contexto?: string }) => {
  const { pathname } = useLocation()
  const enlace = enlaceDeAyuda(contexto ?? pantallaDe(pathname))
  const correo = correoDeAyuda()
  if (!enlace) return <>{children}</>
  return (
    <>
      <a className={ENLACE} href={enlace} rel="noreferrer" target="_blank">
        {children}
      </a>
      {correo && !enlace.startsWith('mailto:') && (
        <>
          {' '}
          (o a{' '}
          <a className={ENLACE} href={`mailto:${correo}`}>
            {correo}
          </a>
          )
        </>
      )}
    </>
  )
}

/**
 * Un texto que llega hecho —casi siempre del núcleo— se pinta con su ayuda enlazada:
 *
 * - si trae una dirección («…escríbenos en https://wixy.app/es/contacto.»), la dirección
 *   es el enlace, sin el punto final de la frase;
 * - si solo dice «escríbenos», esa palabra lleva al formulario de ayuda.
 *
 * Lo que no es un texto (un elemento ya montado) se deja como está.
 */
export const conAyuda = (texto: ReactNode): ReactNode => {
  // Un texto y un botón detrás (`{aviso.texto}{reintentar}`): se mira cada trozo.
  if (Array.isArray(texto)) return texto.map((trozo, i) => <Fragment key={i}>{typeof trozo === 'string' ? conAyuda(trozo) : (trozo as ReactNode)}</Fragment>)
  if (typeof texto !== 'string') return texto
  if (texto.includes('https://')) {
    return texto.split(/(https:\/\/[^\s<>"«»]+)/).map((trozo, i) => {
      if (!trozo.startsWith('https://')) return trozo
      const [, url = trozo, resto = ''] = /^(.*?)([.,;:)]*)$/.exec(trozo) ?? []
      return (
        <span key={i}>
          <a className="font-medium underline underline-offset-2" href={url} rel="noreferrer" target="_blank">
            {url.replace(/^https:\/\//, '')}
          </a>
          {resto}
        </span>
      )
    })
  }
  if (!/escr[ií]benos/i.test(texto)) return texto
  return texto.split(/(escr[ií]benos)/i).map((trozo, i) => (/^escr[ií]benos$/i.test(trozo) ? <Escribenos key={i}>{trozo}</Escribenos> : trozo))
}

/**
 * «Ayuda», a mano en todas las pantallas con sesión: abajo a la derecha, sin tapar nada
 * importante y con su texto a la vista (no solo un icono). Lleva al formulario de
 * contacto con la pantalla de la que viene.
 */
export const BotonDeAyuda = () => {
  const { pathname } = useLocation()
  if (pathname.startsWith('/entrar')) return null
  return (
    <a
      aria-label="Ayuda: escríbenos (se abre en otra pestaña)"
      className="fixed right-4 bottom-4 z-20 inline-flex h-9 items-center gap-1.5 rounded-full border border-linea bg-papel px-3.5 text-[13px] font-medium text-tinta-2 shadow-alta transition-colors hover:border-violeta-borde hover:text-violeta focus-visible:outline-2 focus-visible:outline-violeta print:hidden"
      href={enlaceDeAyuda(pantallaDe(pathname))}
      rel="noreferrer"
      target="_blank"
    >
      <LifeBuoy aria-hidden className="size-4" />
      Ayuda
    </a>
  )
}
